<template>
  <div class="dark-theme">
    <div class="game-container">
      <!-- Header with game stats -->
      <div class="game-header">
        <div class="timer" :class="{ 'timer-warning': timer <= 5 }">
          {{ timer }}s
        </div>
        <div class="question-price">
          Цена вопроса: {{ formatCurrency(currentQuestion.price) }}
        </div>
      </div>

      <!-- Level progress bar -->
      <div class="level-progress">
        <div class="levels">
          <div
            v-for="(level, index) in levels"
            :key="index"
            class="level"
            :class="{
              active: index <= currentLevelIndex,
              completed: index < currentLevelIndex,
              'level-transition': levelTransition && index === currentLevelIndex
            }"
          >
            <div class="level-content">
              <div class="level-price">{{ formatCurrency(level.price) }}</div>
              <div class="level-name">{{ level.name }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Players section -->
      <div class="players-container">
        <div 
          v-for="player in players" 
          :key="player.id"
          class="player-zone"
          :class="{ 'player-active': player.isActive }"
        >
          <img :src="player.avatar" :alt="player.name" class="player-avatar" />
          <h3 class="player-name">{{ player.name }}</h3>
          <div class="player-coins">{{ formatCurrency(player.coins) }}</div>
        </div>
      </div>

      <!-- Question and answers section -->
      <div class="question-area">
        <h2 class="question-text">{{ currentQuestion.question }}</h2>
        <div class="answers-grid">
          <button
            v-for="(answer, index) in currentQuestion.answers"
            :key="index"
            class="answer-button"
            :class="{
              selected: selectedAnswer === index,
              correct: showResults && index === currentQuestion.correctAnswer,
              incorrect: showResults && selectedAnswer === index && index !== currentQuestion.correctAnswer,
              disabled: selectedAnswer !== null
            }"
            @click="selectAnswer(index)"
            :disabled="selectedAnswer !== null"
          >
            <span class="answer-letter">{{ ['A', 'B', 'C', 'D'][index] }}</span>
            {{ answer }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "QuizGame",
  data() {
    return {
      timer: 15,
      selectedAnswer: null,
      showResults: false,
      currentLevelIndex: 0,
      levelTransition: false,
      levels: [
        { name: "1-й уровень", price: 100 },
        { name: "2-й уровень", price: 1500 },
        { name: "3-й уровень", price: 2500 },
        { name: "4-й уровень", price: 3500 },
        { name: "5-й уровень", price: 4500 },
        { name: "6-й уровень", price: 5500 },
        { name: "7-й уровень", price: 6500 },
        { name: "8-й уровень", price: 7500 },
        { name: "9-й уровень", price: 9000 },
        { name: "10-й уровень", price: 10000 },
      ],
      questions: [
        {
          question: "Какой океан самый большой?",
          answers: ["Атлантический", "Тихий", "Индийский", "Арктический"],
          correctAnswer: 1,
          price: 1000,
        },
        {
          question: "Сколько планет в Солнечной системе?",
          answers: ["8", "9", "10", "7"],
          correctAnswer: 0,
          price: 2000,
        },
        {
          question: "Какой язык самый распространённый в мире?",
          answers: ["Английский", "Китайский", "Испанский", "Русский"],
          correctAnswer: 1,
          price: 3000,
        },
        {
          question: "Что является столицей Японии?",
          answers: ["Пекин", "Токио", "Сеул", "Бангкок"],
          correctAnswer: 1,
          price: 5000,
        },
        {
          question: "Кто написал 'Войну и мир'?",
          answers: ["Достоевский", "Пушкин", "Толстой", "Гоголь"],
          correctAnswer: 2,
          price: 10000,
        },
      ],
      currentQuestionIndex: 0,
      players: [
        { id: 1, name: "Player 1", avatar: "avatar.png", coins: 1000, isActive: true },
        { id: 2, name: "Player 2", avatar: "avatar.png", coins: 800, isActive: false },
        { id: 3, name: "Player 3", avatar: "avatar.png", coins: 600, isActive: false },
      ],
      countdownInterval: null,
    };
  },
  computed: {
    currentQuestion() {
      return this.questions[this.currentQuestionIndex];
    },
  },
  methods: {
    formatCurrency(value) {
      return new Intl.NumberFormat('ru-RU').format(value) + ' ₽';
    },
    selectAnswer(index) {
      if (this.selectedAnswer !== null) return;

      this.selectedAnswer = index;
      this.showResults = true;
      clearInterval(this.countdownInterval);

      const isCorrect = index === this.currentQuestion.correctAnswer;

      if (isCorrect) {
        this.handleCorrectAnswer();
      } else {
        this.handleIncorrectAnswer();
      }

      setTimeout(this.moveToNextQuestion, 2000);
    },
    handleCorrectAnswer() {
      this.levelTransition = true;
      this.currentLevelIndex = Math.min(this.currentLevelIndex + 1, this.levels.length - 1);
      
      const activePlayer = this.players.find(p => p.isActive);
      if (activePlayer) {
        activePlayer.coins += this.currentQuestion.price;
      }
    },
    handleIncorrectAnswer() {
      const activePlayer = this.players.find(p => p.isActive);
      if (activePlayer) {
        activePlayer.coins = Math.max(0, activePlayer.coins - Math.floor(this.currentQuestion.price / 2));
      }
    },
    moveToNextQuestion() {
      this.currentQuestionIndex = (this.currentQuestionIndex + 1) % this.questions.length;
      this.selectedAnswer = null;
      this.showResults = false;
      this.timer = 15;
      this.levelTransition = false;
      this.startTimer();
      this.rotateActivePlayer();
    },
    rotateActivePlayer() {
      const currentActiveIndex = this.players.findIndex(p => p.isActive);
      this.players.forEach((player, index) => {
        player.isActive = index === (currentActiveIndex + 1) % this.players.length;
      });
    },
    startTimer() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }
      
      this.countdownInterval = setInterval(() => {
        if (this.timer > 0) {
          this.timer--;
        } else {
          clearInterval(this.countdownInterval);
          if (this.selectedAnswer === null) {
            this.selectAnswer(-1); // Auto-select wrong answer when time runs out
          }
        }
      }, 1000);
    },
  },
  mounted() {
    this.startTimer();
  },
  beforeUnmount() {
    clearInterval(this.countdownInterval);
  },
};
</script>

<style scoped>
.dark-theme {
  background: #121212;
  color: #fff;
  min-height: 100vh;
  padding: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.game-container {
  max-width: 1200px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.game-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.timer {
  font-size: 2rem;
  font-weight: bold;
  color: #4caf50;
  transition: color 0.3s ease;
}

.timer-warning {
  color: #ff5252;
  animation: pulse 1s infinite;
}

.question-price {
  font-size: 1.5rem;
  color: #ffd700;
  font-weight: bold;
}

.level-progress {
  background: #1e1e1e;
  padding: 1rem;
  border-radius: 12px;
  overflow-x: auto;
}

.levels {
  display: flex;
  gap: 0.5rem;
  min-width: max-content;
}

.level {
  background: #333;
  border-radius: 8px;
  padding: 0.75rem;
  min-width: 100px;
  transition: all 0.3s ease;
}

.level-content {
  text-align: center;
}

.level.active {
  background: #4caf50;
  transform: scale(1.05);
}

.level.completed {
  background: #1b5e20;
}

.level-transition {
  animation: levelUp 0.5s ease-out;
}

.level-price {
  color: #ffd700;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
}

.level-name {
  font-size: 0.7rem;
  font-weight: bold;
}

.players-container {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.player-zone {
  background: #1e1e1e;
  border-radius: 12px;
  padding: 1.5rem;
  min-width: 200px;
  text-align: center;
  transition: all 0.3s ease;
}

.player-active {
  box-shadow: 0 0 15px rgba(76, 175, 80, 0.5);
  transform: scale(1.05);
}

.player-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin-bottom: 1rem;
  border: 3px solid #333;
}

.player-name {
  margin: 0.5rem 0;
  font-size: 1.2rem;
}

.player-coins {
  color: #ffd700;
  font-size: 1.1rem;
  font-weight: bold;
}

.question-area {
  background: #1e1e1e;
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
}

.question-text {
  font-size: 1.5rem;
  margin-bottom: 2rem;
  line-height: 1.4;
}

.answers-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.answer-button {
  position: relative;
  padding: 1rem;
  border: none;
  border-radius: 8px;
  background: #333;
  color: #fff;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.answer-letter {
  background: rgba(255, 255, 255, 0.1);
  padding: 0.5rem;
  border-radius: 4px;
  min-width: 2.5rem;
}

.answer-button:hover:not(.disabled) {
  background: #444;
  transform: translateY(-2px);
}

.answer-button.selected {
  background: #2196f3;
}

.answer-button.correct {
  background: #4caf50;
}

.answer-button.incorrect {
  background: #f44336;
}

.answer-button.disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

@keyframes levelUp {
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1.05); }
}

@media (max-width: 768px) {
  .dark-theme {
    padding: 1rem;
  }

  .game-container {
    gap: 1rem;
  }

  .question-text {
    font-size: 1.2rem;
  }

  .answers-grid {
    grid-template-columns: 1fr;
  }

  .player-zone {
    min-width: 150px;
    padding: 1rem;
  }

  .player-avatar {
    width: 60px;
    height: 60px;
  }
}
</style>